import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class MarylunaCartDrawerComponent extends Component {
  #revision = 0;
  #applyingDiscount = false;
  #upsellItems = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('maryluna:cart:added', this.#onAdded);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    this.#applyDiscount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('maryluna:cart:added', this.#onAdded);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    if (this.refs.dialog.open) document.documentElement.removeAttribute('scroll-lock');
  }

  open(event) {
    event?.preventDefault?.();
    const dialog = this.refs.dialog;
    if (dialog.open) return;
    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.addEventListener('close', () => document.documentElement.removeAttribute('scroll-lock'), { once: true });
    this.#fillUpsell();
  }

  close() {
    this.refs.dialog.close();
  }

  onDialogClick(event) {
    if (event.composedPath()[0] === this.refs.dialog) this.close();
  }

  async remove(event) {
    const button = event.target;
    const index = [...this.querySelectorAll('.ml-cart__remove')].indexOf(button);
    button.disabled = true;
    const revision = ++this.#revision;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: button.dataset.key, quantity: 0, sections: [this.dataset.sectionId], sections_url: window.location.pathname }),
      });
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      if (revision !== this.#revision) return;
      this.#render(data.sections?.[this.dataset.sectionId]);
      const buttons = this.querySelectorAll('.ml-cart__remove');
      (buttons[Math.min(index, buttons.length - 1)] ?? this.querySelector('.ml-cart__close'))?.focus();
    } catch {
      button.disabled = false;
    }
  }

  async addUpsell(event) {
    const button = event.target;
    button.disabled = true;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }], sections: [this.dataset.sectionId], sections_url: window.location.pathname }),
      });
      if (!response.ok) throw new Error(response.statusText);
      document.dispatchEvent(new CustomEvent('maryluna:cart:added', { detail: await response.json() }));
    } catch {
      button.disabled = false;
    }
  }

  #fillUpsell() {
    const upsell = this.querySelector('[data-upsell-url]');
    if (!upsell || !this.refs.dialog.open) return;
    this.#upsellItems ??= this.#fetchUpsell(upsell.dataset);
    this.#upsellItems.then(
      (items) => {
        if (!items.length || !upsell.isConnected) return;
        upsell.querySelector('[data-track]').replaceChildren(...items.map((item) => item.cloneNode(true)));
        upsell.hidden = false;
      },
      () => {
        this.#upsellItems = null;
      }
    );
  }

  async #fetchUpsell({ upsellUrl, upsellPages }) {
    const url = new URL(upsellUrl, window.location.origin);
    url.searchParams.set('section_id', 'maryluna-cart-upsell');
    url.searchParams.set('page', this.#upsellPage(Number(upsellPages)));
    const response = await fetch(url);
    if (!response.ok) throw new Error(response.statusText);
    return [...new DOMParser().parseFromString(await response.text(), 'text/html').querySelectorAll('.ml-cart__upsell-item')];
  }

  #upsellPage(pages) {
    const random = String(Math.floor(Math.random() * pages) + 1);
    try {
      const stored = Number(sessionStorage.getItem('maryluna-upsell-page'));
      const page = stored >= 1 && stored <= pages ? String(stored) : random;
      sessionStorage.setItem('maryluna-upsell-page', page);
      return page;
    } catch {
      return random;
    }
  }

  #onAdded = async (event) => {
    const html = event.detail?.sections?.[this.dataset.sectionId];
    if (html) {
      this.#revision++;
      this.#render(html);
    } else {
      await this.#refresh().catch(() => {});
    }
    this.open();
  };

  #onCartLinesUpdate = async (event) => {
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    try {
      const result = await event.promise;
      if (result?.detail?.didError) return;
      await this.#refresh();
    } catch {
      return;
    }
    if (event.action !== 'add') return;
    if (sourceModal?.open) {
      sourceModal.addEventListener('close', () => this.open(), { once: true });
    } else {
      this.open();
    }
  };

  async #refresh() {
    const revision = ++this.#revision;
    const sectionId = this.dataset.sectionId;
    const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`);
    if (!response.ok) throw new Error(response.statusText);
    const sections = await response.json();
    if (revision === this.#revision) this.#render(sections[sectionId]);
  }

  async #applyDiscount() {
    const inner = this.querySelector('.ml-cart__inner');
    const code = inner?.dataset.discountCode;
    if (!code || this.#applyingDiscount || this.#attempted(code)) return;
    this.#applyingDiscount = true;
    const revision = ++this.#revision;
    const codes = [...(inner.dataset.discountCodes || '').split(',').filter(Boolean), code];
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: [this.dataset.sectionId], sections_url: window.location.pathname }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (revision === this.#revision) this.#render(data.sections?.[this.dataset.sectionId]);
    } catch {
    } finally {
      this.#applyingDiscount = false;
    }
  }

  #attempted(code) {
    try {
      const key = `maryluna-discount-${code.toLowerCase()}`;
      const count = Number(sessionStorage.getItem(key) || 0);
      sessionStorage.setItem(key, String(count + 1));
      return count >= 3;
    } catch {
      return false;
    }
  }

  #render(markup) {
    if (!markup) return;
    const content = new DOMParser().parseFromString(markup, 'text/html').querySelector('.ml-cart__inner');
    if (!content) return;
    this.querySelector('.ml-cart__inner')?.replaceWith(content);
    const count = Number(content.dataset.cartCount || 0);
    for (const element of document.querySelectorAll('[data-header-cart-count]')) {
      element.textContent = String(count);
      element.hidden = count === 0;
    }
    this.#applyDiscount();
    this.#fillUpsell();
    if (this.refs.status) this.refs.status.textContent = content.querySelector('.ml-cart__title')?.textContent.replace(/\s+/g, ' ').trim() ?? '';
  }
}

if (!customElements.get('maryluna-cart-drawer-component')) {
  customElements.define('maryluna-cart-drawer-component', MarylunaCartDrawerComponent);
}
